import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import BrandProvider from "@/contexts/BrandContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import PublicDemo from "@/pages/PublicDemo";
import WatchDemo from "@/pages/WatchDemo";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import InviteAccept from "@/pages/InviteAccept";
import TeamInviteAccept from "@/pages/TeamInviteAccept";

import CoachLayout from "@/pages/coach/CoachLayout";
import CoachDashboard from "@/pages/coach/CoachDashboard";
import CoachRoster from "@/pages/coach/CoachRoster";
import CoachPlayerDetail from "@/pages/coach/CoachPlayerDetail";
import MotionIQStudioPage from "@/pages/coach/MotionIQStudioPage";
import CoachCalendar from "@/pages/coach/CoachCalendar";
import CoachSettings from "@/pages/coach/CoachSettings";
import CoachCSV from "@/pages/coach/CoachCSV";
import CoachWorkload from "@/pages/coach/CoachWorkload";
import CoachReplays from "@/pages/coach/CoachReplays";
import PlayerPortal from "@/pages/player/PlayerPortal";
import PlayerHistory from "@/pages/player/PlayerHistory";
import PlayerAppleHealth from "@/pages/player/PlayerAppleHealth";
import PlayerLivup from "@/pages/player/PlayerLivup";
import PlayerTelemetry from "@/pages/player/PlayerTelemetry";
import PlayerWarmup from "@/pages/player/PlayerWarmup";
import PlayerRoutineShell from "@/pages/player/PlayerRoutineShell";
import PlayerRecoveryShell from "@/pages/player/PlayerRecoveryShell";
import PlayerGuideShell from "@/pages/player/PlayerGuideShell";
import PlayerTodaysPlan from "@/pages/player/PlayerTodaysPlan";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import PreThrowingRoutine from "@/pages/PreThrowingRoutine";
import RecoveryRoutine from "@/pages/RecoveryRoutine";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import CoachLessonOfferings from "@/pages/coach/CoachLessonOfferings";
import LessonsMarketplace from "@/pages/LessonsMarketplace";
import LessonsCoachDetail from "@/pages/LessonsCoachDetail";
import LessonsBookingSuccess from "@/pages/LessonsBookingSuccess";
import PlayerBookings from "@/pages/player/PlayerBookings";
import CoachGuide from "@/pages/help/CoachGuide";
import Pricing from "@/pages/Pricing";
import EnterpriseContact from "@/pages/EnterpriseContact";
import HighSchoolPage from "@/pages/segments/HighSchoolPage";
import CollegePage from "@/pages/segments/CollegePage";
import ProfessionalPage from "@/pages/segments/ProfessionalPage";
import ExecutiveDashboard from "@/pages/coach/ExecutiveDashboard";
import EnterpriseOnboarding from "@/pages/coach/EnterpriseOnboarding";
import SoloSignup from "@/pages/SoloSignup";
import AcademySignup from "@/pages/academy/AcademySignup";
import AcademySuccess from "@/pages/academy/AcademySuccess";
import AcademyDashboard from "@/pages/academy/AcademyDashboard";
import CoachLiveCoach from "@/pages/livecoach/CoachLiveCoach";
import PlayerLiveCoach from "@/pages/livecoach/PlayerLiveCoach";
import LiveRoom from "@/pages/livecoach/LiveRoom";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCancel from "@/pages/BillingCancel";
import WhyArmIQ from "@/pages/WhyArmIQ";
import SoloBilling from "@/pages/player/SoloBilling";
import OnboardingScreening from "@/pages/OnboardingScreening";
import PitchMechanicsPage from "@/pages/PitchMechanicsPage";
import RecruiterCardPage from "@/pages/RecruiterCardPage";
import SpeedIQPage from "@/pages/SpeedIQPage";
import PlayerSpeedIQHistory from "@/pages/player/PlayerSpeedIQHistory";
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import RefundPolicy from "@/pages/legal/RefundPolicy";
import CookieConsent from "@/components/CookieConsent";

// BioHacker Sports™ — new IA (Feb 2026 rebrand)
import Platform from "@/pages/Platform";
import Solutions from "@/pages/Solutions";
import Sports from "@/pages/Sports";
import BaseballPackage from "@/pages/sports/BaseballPackage";
import SportPackagePlaceholder from "@/pages/sports/SportPackagePlaceholder";
import Technology from "@/pages/Technology";
import Enterprise from "@/pages/Enterprise";
import Resources from "@/pages/Resources";
import RequestDemo from "@/pages/RequestDemo";
import MentalPerformance from "@/pages/MentalPerformance";
import Compliance from "@/pages/Compliance";
import HumeShop from "@/pages/shop/HumeShop";
import HumeShopSuccess from "@/pages/shop/HumeShopSuccess";
import ForAthleticDirectors from "@/pages/for/ForAthleticDirectors";
import ForCoachingStaff from "@/pages/for/ForCoachingStaff";
import ForAthleticTrainers from "@/pages/for/ForAthleticTrainers";
import ForSportsMedicine from "@/pages/for/ForSportsMedicine";
import FusionLanding from "@/pages/fusion/FusionLanding";
import FusionTeamPage from "@/pages/fusion/FusionTeamPage";
import FusionAthletePage from "@/pages/fusion/FusionAthletePage";
import FusionPledgeSuccess from "@/pages/fusion/FusionPledgeSuccess";
import FusionJoinPage from "@/pages/fusion/FusionJoinPage";
import FusionLogin from "@/pages/fusion/FusionLogin";
import FusionEmbed from "@/pages/fusion/FusionEmbed";
import PlayerGuide from "@/pages/PlayerGuide";
import CoachFusion from "@/pages/coach/CoachFusion";
import PlayerFusion from "@/pages/player/PlayerFusion";

function AppRouter() {
  const loc = useLocation();
  if (loc.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/team-invite/:token" element={<TeamInviteAccept />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/enterprise/contact" element={<EnterpriseContact />} />
      <Route path="/platform" element={<Platform />} />
      <Route path="/solutions" element={<Solutions />} />
      <Route path="/sports" element={<Sports />} />
      <Route path="/sports/baseball" element={<BaseballPackage />} />
      <Route path="/sports/:sportId" element={<SportPackagePlaceholder />} />
      <Route path="/technology" element={<Technology />} />
      <Route path="/enterprise" element={<Enterprise />} />
      <Route path="/resources" element={<Resources />} />
      <Route path="/request-demo" element={<RequestDemo />} />
      <Route path="/mental-performance" element={<MentalPerformance />} />
      <Route path="/compliance" element={<Compliance />} />
      <Route path="/trust" element={<Compliance />} />
      <Route path="/shop/hume" element={<HumeShop />} />
      <Route path="/shop/hume/success" element={<HumeShopSuccess />} />
      <Route path="/for/athletic-directors" element={<ForAthleticDirectors />} />
      <Route path="/for/coaching-staff" element={<ForCoachingStaff />} />
      <Route path="/for/athletic-trainers" element={<ForAthleticTrainers />} />
      <Route path="/for/sports-medicine" element={<ForSportsMedicine />} />
      <Route path="/fusion" element={<FusionLanding />} />
      <Route path="/fusion/team/:slug" element={<FusionTeamPage />} />
      <Route path="/fusion/a/:slug" element={<FusionAthletePage />} />
      <Route path="/fusion/pledge/success" element={<FusionPledgeSuccess />} />
      <Route path="/fusion/join/:code" element={<FusionJoinPage />} />
      <Route path="/fusion/login" element={<FusionLogin />} />
      <Route path="/fusion/embed/:slug" element={<FusionEmbed />} />
      <Route path="/player-guide" element={<PlayerGuide />} />
      <Route path="/high-school" element={<HighSchoolPage />} />
      <Route path="/college" element={<CollegePage />} />
      <Route path="/professional" element={<ProfessionalPage />} />
      <Route path="/demo/:athleteId/:dayId" element={<PublicDemo />} />
      <Route path="/demo/:athleteId" element={<PublicDemo />} />
      <Route path="/watch" element={<WatchDemo />} />
      <Route path="/watch-demo" element={<WatchDemo />} />
      <Route path="/solo" element={<SoloSignup />} />
      <Route path="/academy" element={<AcademySignup />} />
      <Route path="/academy/success" element={<AcademySuccess />} />
      <Route path="/live/:apptId" element={<ProtectedRoute><LiveRoom /></ProtectedRoute>} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/billing/cancel" element={<BillingCancel />} />
      <Route path="/why" element={<WhyArmIQ />} />
      <Route path="/lessons" element={<LessonsMarketplace />} />
      <Route path="/lessons/coach/:coachId" element={<LessonsCoachDetail />} />
      <Route path="/lessons/success" element={<LessonsBookingSuccess />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/portal/lessons" element={<ProtectedRoute role="player"><PlayerBookings /></ProtectedRoute>} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/portal/billing" element={<ProtectedRoute role="player"><SoloBilling /></ProtectedRoute>} />
      <Route path="/onboarding/screening" element={<ProtectedRoute><OnboardingScreening /></ProtectedRoute>} />
      <Route path="/pitch-mechanics" element={<ProtectedRoute><PitchMechanicsPage /></ProtectedRoute>} />
      <Route path="/speediq" element={<ProtectedRoute><SpeedIQPage /></ProtectedRoute>} />
      <Route path="/portal/speediq/history" element={<ProtectedRoute role="player"><PlayerSpeedIQHistory /></ProtectedRoute>} />
      <Route path="/share/capture/:token" element={<RecruiterCardPage />} />

      <Route path="/dashboard" element={<ProtectedRoute role="coach"><CoachLayout /></ProtectedRoute>}>
        <Route index element={<CoachDashboard />} />
        <Route path="roster" element={<CoachRoster />} />
        <Route path="player/:playerId" element={<CoachPlayerDetail />} />
        <Route path="motioniq" element={<MotionIQStudioPage />} />
        <Route path="motioniq/:captureId" element={<MotionIQStudioPage />} />
        <Route path="fusion" element={<CoachFusion />} />
        <Route path="calendar" element={<CoachCalendar />} />
        <Route path="routine" element={<PreThrowingRoutine />} />
        <Route path="recovery" element={<RecoveryRoutine />} />
        <Route path="workload" element={<CoachWorkload />} />
        <Route path="replays" element={<CoachReplays />} />
        <Route path="lessons" element={<CoachLessonOfferings />} />
        <Route path="livecoach" element={<CoachLiveCoach />} />
        <Route path="academy" element={<AcademyDashboard />} />
        <Route path="settings" element={<CoachSettings />} />
        <Route path="executive" element={<ExecutiveDashboard />} />
        <Route path="enterprise/onboarding" element={<EnterpriseOnboarding />} />
        <Route path="import" element={<CoachCSV />} />
        <Route path="csv" element={<Navigate to="/dashboard/import" replace />} />
        <Route path="help" element={<CoachGuide />} />
      </Route>

      <Route path="/portal" element={<ProtectedRoute role="player"><PlayerPortal /></ProtectedRoute>} />
      <Route path="/portal/fusion" element={<ProtectedRoute role="player"><PlayerFusion /></ProtectedRoute>} />
      <Route path="/portal/livecoach" element={<ProtectedRoute role="player"><PlayerLiveCoach /></ProtectedRoute>} />
      <Route path="/portal/history" element={<ProtectedRoute role="player"><PlayerHistory /></ProtectedRoute>} />
      <Route path="/portal/apple-health" element={<ProtectedRoute role="player"><PlayerAppleHealth /></ProtectedRoute>} />
      <Route path="/portal/livup" element={<ProtectedRoute role="player"><PlayerLivup /></ProtectedRoute>} />
      <Route path="/portal/telemetry" element={<ProtectedRoute role="player"><PlayerTelemetry /></ProtectedRoute>} />
      <Route path="/portal/warmup" element={<ProtectedRoute role="player"><PlayerWarmup /></ProtectedRoute>} />
      <Route path="/portal/routine" element={<ProtectedRoute role="player"><PlayerRoutineShell /></ProtectedRoute>} />
      <Route path="/portal/recovery" element={<ProtectedRoute role="player"><PlayerRecoveryShell /></ProtectedRoute>} />
      <Route path="/portal/help" element={<ProtectedRoute role="player"><PlayerGuideShell /></ProtectedRoute>} />
      <Route path="/portal/plan" element={<ProtectedRoute role="player"><PlayerTodaysPlan /></ProtectedRoute>} />

      <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
    </Routes>
  );
}

function CookieConsentGate() {
  const { pathname } = useLocation();
  return pathname.startsWith("/fusion/embed") ? null : <CookieConsent />;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <BrandProvider>
            <AppRouter />
            <CookieConsentGate />
            <Toaster position="top-right" richColors theme="dark" />
          </BrandProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
